
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { iconList } from './iconList';
import Login from './Login';
import { LogIn, LogOut } from 'lucide-react';
import Navbar from './components/layout/Navbar';
import AboutSection from './components/sections/AboutSection';
import HeroSection from './components/sections/HeroSection';
import ExperienceSection from './components/sections/ExperienceSection';
import ProjectsSection from './components/sections/ProjectsSection';
import ContactSection from './components/sections/ContactSection';
import Footer from './components/layout/Footer';
import { translations } from './data/translations';

import Cropper from 'react-easy-crop';
import getCroppedImg from './utils/cropImage';

// Estado inicial limpio - Los datos reales vienen de Supabase
const initialState = {
    personal: {
        name: '',
        title: { es: '', pt: '', en: '' },
        email: '',
        phone: '',
        location: '',
        bio: { es: '', pt: '', en: '' },
        bio2: { es: '', pt: '', en: '' },
        github: '',
        linkedin: '',
        website: ''
    },
    skills: [],
    languages: [],
    experience: [],
    education: [],
    projects: []
};


export default function DynamicPortfolio() {
    const [isAdmin, setIsAdmin] = useState(false); // Can keep for UI state, but derived from session
    const [session, setSession] = useState(null);
    const [showLogin, setShowLogin] = useState(false);

    const [scrollProgress, setScrollProgress] = useState(0);
    const [currentLang, setCurrentLang] = useState('es');
    const [editingPersonal, setEditingPersonal] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);


    // Icon Search State
    const [iconSearchTerm, setIconSearchTerm] = useState('');
    const [suggestedIcons, setSuggestedIcons] = useState([]);
    const [selectedIcon, setSelectedIcon] = useState('');
    const [customIconFile, setCustomIconFile] = useState(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [saving, setSaving] = useState(false);

    // Cropper State
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [photoToCrop, setPhotoToCrop] = useState(null);
    const [showCropper, setShowCropper] = useState(false);
    const [cropperTarget, setCropperTarget] = useState(null); // 'personal' or 'project'
    const [projectImageFile, setProjectImageFile] = useState(null);


    // Traducciones estáticas para UI
    // Traducciones estáticas para UI están ahora en src/data/translations.js

    const t = translations[currentLang];





    const [portfolio, setPortfolio] = useState(initialState);
    const [techIcons, setTechIcons] = useState({});
    const [showAddProject, setShowAddProject] = useState(false);
    const [showAddSkill, setShowAddSkill] = useState(false);
    const [showAddExperience, setShowAddExperience] = useState(false);
    const [tempPersonal, setTempPersonal] = useState({});

    // Editing State
    const [editingSkillId, setEditingSkillId] = useState(null);
    const [editingProjectId, setEditingProjectId] = useState(null);
    const [editingExperienceId, setEditingExperienceId] = useState(null);

    // Estados para formularios multilingües
    const [newProject, setNewProject] = useState({
        title: { es: '', pt: '', en: '' },
        tech: '',
        description: { es: '', pt: '', en: '' },
        link: '',
        image_url: ''
    });
    const [newSkill, setNewSkill] = useState({ name: '', level: 50 });
    const [newExperience, setNewExperience] = useState({
        title: { es: '', pt: '', en: '' },
        company: '',
        period: '',
        description: { es: [''], pt: [''], en: [''] }
    });

    // Fetch data from Supabase
    const fetchData = useCallback(async () => {

        try {
            const [personalRes, skillsRes, expRes, projectsRes, langRes, eduRes, techRes] = await Promise.all([
                supabase.from('personal_info').select('*').single(),
                supabase.from('skills').select('*').order('id'),
                supabase.from('experience').select('*').order('id'),
                supabase.from('projects').select('*').order('id'),
                supabase.from('languages').select('*').order('id'),
                supabase.from('education').select('*').order('id'),
                supabase.from('technology_icons').select('*')
            ]);

            // Construct portfolio state
            const newPortfolio = { ...initialState };

            if (personalRes.data) newPortfolio.personal = personalRes.data;
            if (skillsRes.data) newPortfolio.skills = skillsRes.data;
            if (expRes.data) newPortfolio.experience = expRes.data;
            if (projectsRes.data) newPortfolio.projects = projectsRes.data;
            if (langRes.data) newPortfolio.languages = langRes.data;
            if (eduRes.data) newPortfolio.education = eduRes.data;

            // For now, let's use what we got, falling back to initialState if DB is empty but structurally valid
            if (personalRes.data) setPortfolio(newPortfolio);
            if (techRes.data) {
                const iconsMap = techRes.data.reduce((acc, item) => ({ ...acc, [item.name]: item.icon_class }), {});
                setTechIcons(iconsMap);
            }

        } catch (error) {
            console.error('Error fetching data:', error);
        }

    }, []);

    useEffect(() => {
        // Auth Listener
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            setIsAdmin(!!session);
        });

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            setIsAdmin(!!session);
        });

        const handleScroll = () => {
            const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
            const currentScroll = window.scrollY;
            setScrollProgress((currentScroll / totalHeight) * 100);
            setScrolled(currentScroll > 50);

            const animatedElements = document.querySelectorAll('.animate-on-scroll');
            animatedElements.forEach(el => {
                const rect = el.getBoundingClientRect();
                if (rect.top <= window.innerHeight * 0.75) {
                    el.classList.add('opacity-100', 'translate-y-0');
                    el.classList.remove('opacity-0', 'translate-y-10');
                }
            });
        };

        window.addEventListener('scroll', handleScroll);

        fetchData();

        // Detectar idioma del navegador
        const savedLang = localStorage.getItem('preferred_lang');
        if (savedLang) {
            setCurrentLang(savedLang);
        } else {
            // Detectar idioma del navegador
            const browserLang = navigator.language || navigator.userLanguage;
            let detectedLang = 'en'; // Default

            if (browserLang.startsWith('es')) {
                detectedLang = 'es';
            } else if (browserLang.startsWith('pt')) {
                detectedLang = 'pt';
            }

            setCurrentLang(detectedLang);
            localStorage.setItem('preferred_lang', detectedLang);
        }

        // Activar animaciones iniciales inmediatamente
        setTimeout(() => {
            handleScroll(); // Trigger initial animation check
        }, 100);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            subscription.unsubscribe();
        };
    }, [fetchData]);

    useEffect(() => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/devicon.min.css';
        document.head.appendChild(link);
    }, []);

    // We don't save entire portfolio anymore, we save individual items
    // const savePortfolio = (newData) => { ... } // Removed

    const changeLang = (lang) => {
        setCurrentLang(lang);
        localStorage.setItem('preferred_lang', lang);
    };

    // Helper para obtener texto seguro
    const getText = (obj, lang) => {
        if (!obj) return '';
        if (typeof obj === 'string') return obj; // Fallback para datos antiguos
        return obj[lang] || obj['es'] || '';
    };

    // Navegación suave sin hash en URL
    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };


    const addProject = async () => {
        if (!newProject.title.es) {
            alert("El título del proyecto es obligatorio.");
            return;
        }

        // Sanitize payload: remove ID and created_at, ensure tags/tech consistency
        const projectPayload = { ...newProject };
        delete projectPayload.id;
        delete projectPayload.created_at;
        // If your DB uses 'tags', ensure we aren't sending 'tech' if it's not in DB
        delete projectPayload.tech;

        // Check if updating
        if (editingProjectId) {
            const { data, error } = await supabase.from('projects')
                .update(projectPayload)
                .eq('id', editingProjectId)
                .select();

            if (error) {
                console.error("Error updating project:", error);
                alert("Error al actualizar proyecto: " + error.message);
                return;
            }

            if (data) {
                setPortfolio(prev => ({
                    ...prev,
                    projects: prev.projects.map(p => p.id === editingProjectId ? data[0] : p)
                }));
                resetProjectForm();
            }
        } else {
            // Insert new
            const { data, error } = await supabase.from('projects').insert([projectPayload]).select();

            if (error) {
                console.error("Error creating project:", error);
                alert("Error al crear proyecto: " + error.message);
                return;
            }

            if (data) {
                setPortfolio(prev => ({ ...prev, projects: [...prev.projects, data[0]] }));
                resetProjectForm();
            }
        }
    };

    const resetProjectForm = () => {
        setNewProject({
            title: { es: '', pt: '', en: '' },
            tags: '',
            description: { es: '', pt: '', en: '' },
            github_url: '',
            demo_url: '',
            image_url: ''
        });
        setEditingProjectId(null);
        setShowAddProject(false);
        setProjectImageFile(null);
    };

    const handleEditProject = (project) => {
        setNewProject(project);
        setEditingProjectId(project.id);
        setShowAddProject(true);
        setProjectImageFile(null); // Ensure image file input is cleared when opening edit
    };

    const addExperience = async () => {
        if (!newExperience.title.es) return;

        if (editingExperienceId) {
            const { data, error } = await supabase.from('experience')
                .update(newExperience)
                .eq('id', editingExperienceId)
                .select();

            if (!error && data) {
                setPortfolio(prev => ({
                    ...prev,
                    experience: prev.experience.map(e => e.id === editingExperienceId ? data[0] : e)
                }));
                setNewExperience({ title: { es: '', pt: '', en: '' }, company: '', period: '', description: { es: [''], pt: [''], en: [''] } });
                setEditingExperienceId(null);
                setShowAddExperience(false);
            }

        } else {
            const { data, error } = await supabase.from('experience').insert([newExperience]).select();
            if (!error && data) {
                setPortfolio(prev => ({ ...prev, experience: [...prev.experience, data[0]] }));
                setNewExperience({ title: { es: '', pt: '', en: '' }, company: '', period: '', description: { es: [''], pt: [''], en: [''] } });
                setShowAddExperience(false);
            }
        }
    };

    const handleEditExperience = (exp) => {
        setNewExperience(exp);
        setEditingExperienceId(exp.id);
        setShowAddExperience(true);
    };

    // Funciones de eliminación (sin cambios)
    const deleteProject = async (id) => {
        const { error } = await supabase.from('projects').delete().eq('id', id);
        if (!error) {
            setPortfolio(prev => ({ ...prev, projects: prev.projects.filter(p => p.id !== id) }));
        }
    };

    const deleteExperience = async (id) => {
        const { error } = await supabase.from('experience').delete().eq('id', id);
        if (!error) {
            setPortfolio(prev => ({ ...prev, experience: prev.experience.filter(e => e.id !== id) }));
        }
    };

    const addSkill = async () => {
        if (!newSkill.name) return;

        let finalIcon = selectedIcon || 'devicon-code-plain';

        // Handle Custom File Upload
        if (customIconFile) {
            const fileName = `${Date.now()}-${customIconFile.name}`;
            const { error: uploadError } = await supabase.storage
                .from('skill-icons')
                .upload(fileName, customIconFile);

            if (uploadError) {
                console.error("Error uploading icon:", uploadError);
                return;
            }

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('skill-icons')
                .getPublicUrl(fileName);

            finalIcon = publicUrl;
        }

        const skillData = { ...newSkill, icon: finalIcon };

        if (editingSkillId) {
            const { data, error } = await supabase.from('skills')
                .update(skillData)
                .eq('id', editingSkillId)
                .select();

            if (!error && data) {
                setPortfolio(prev => ({
                    ...prev,
                    skills: prev.skills.map(s => s.id === editingSkillId ? data[0] : s)
                }));
                // Reset
                setNewSkill({ name: '', level: 50 });
                setSelectedIcon('');
                setIconSearchTerm('');
                setCustomIconFile(null);
                setEditingSkillId(null);
                setShowAddSkill(false);
            }
        } else {
            const { data, error } = await supabase.from('skills').insert([skillData]).select();

            if (!error && data) {
                setPortfolio(prev => ({ ...prev, skills: [...prev.skills, data[0]] }));
                // Reset
                setNewSkill({ name: '', level: 50 });
                setSelectedIcon('');
                setIconSearchTerm('');
                setCustomIconFile(null); // Reset file
                setShowAddSkill(false);
            }
        }
    };

    const handleEditSkill = (skill) => {
        setNewSkill({ name: skill.name, level: skill.level });
        setSelectedIcon(skill.icon || '');
        setIconSearchTerm(skill.name);
        setEditingSkillId(skill.id);
        setShowAddSkill(true);
    };

    const deleteSkill = async (id) => { // Changed to ID
        const { error } = await supabase.from('skills').delete().eq('id', id);
        if (!error) {
            setPortfolio(prev => ({ ...prev, skills: prev.skills.filter(s => s.id !== id) }));
        }
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const processCroppedImage = async () => {
        if (!photoToCrop || !croppedAreaPixels) {
            alert("Espere a que la imagen cargue completamente.");
            return;
        }

        setUploadingPhoto(true);
        try {
            const croppedImageBlob = await getCroppedImg(
                photoToCrop,
                croppedAreaPixels
            );

            if (!croppedImageBlob) {
                throw new Error("No se pudo generar la imagen recortada.");
            }

            const fileName = `${cropperTarget === 'personal' ? 'profile-photo' : 'project-image'}-${Date.now()}.jpg`;
            const fileToUpload = new File([croppedImageBlob], fileName, { type: "image/jpeg" });

            const { error: uploadError } = await supabase.storage
                .from('portfolio-images') // Use a general bucket for all images
                .upload(fileName, fileToUpload);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('portfolio-images')
                .getPublicUrl(fileName);

            if (cropperTarget === 'personal') {
                setTempPersonal(prev => ({ ...prev, photo_url: publicUrl }));
            } else if (cropperTarget === 'project') {
                setNewProject(prev => ({ ...prev, image_url: publicUrl }));
            }
            setShowCropper(false);
            setPhotoToCrop(null);
            setCropperTarget(null);
        } catch (e) {
            console.error("Error cropping/uploading:", e);
            alert("Error: " + e.message); // Show specific error to user
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleFileSelect = (e, target) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setPhotoToCrop(reader.result);
                setCropperTarget(target);
                setShowCropper(true);
            });
            reader.readAsDataURL(file);
        }
    };

    const savePersonalInfo = async () => {
        setSaving(true);
        try {
            // Ensure we have an ID
            if (!tempPersonal.id) {
                throw new Error("Missing profile ID");
            }

            // Remove id and created_at from the update payload
            const { id, created_at, ...potentialUpdates } = tempPersonal;

            // Detect actual changes
            const updates = {};
            Object.keys(potentialUpdates).forEach(key => {
                const newValue = potentialUpdates[key];
                const oldValue = portfolio.personal[key];

                // Allow for deep comparison of objects (like the translation objects)
                // or simple comparison for primitives
                const isDifferent = typeof newValue === 'object' && newValue !== null
                    ? JSON.stringify(newValue) !== JSON.stringify(oldValue)
                    : newValue !== oldValue;

                if (isDifferent) {
                    updates[key] = newValue;
                }
            });

            if (Object.keys(updates).length === 0) {
                // No changes detected, just close modal
                alert("No hay cambios para guardar.");
                setEditingPersonal(false);
                setSaving(false);
                return;
            }

            const { data, error } = await supabase
                .from('personal_info')
                .update(updates)
                .eq('id', id)
                .select();

            if (error) throw error;

            if (data) {
                setPortfolio(prev => ({ ...prev, personal: data[0] }));
                setEditingPersonal(false);
            }
        } catch (error) {
            console.error("Error saving personal info:", error);
            alert("Error al guardar: " + error.message);
        } finally {
            setSaving(false);
        }
    };

    // Smart Icon Search Logic
    useEffect(() => {
        if (!iconSearchTerm) {
            // Show popular icons by default if search is empty
            const defaults = [
                'devicon-javascript-plain', 'devicon-react-original', 'devicon-html5-plain',
                'devicon-css3-plain', 'devicon-python-plain', 'devicon-java-plain',
                'devicon-nodejs-plain', 'devicon-git-plain'
            ];
            setSuggestedIcons(defaults);
            return;
        }
        const term = iconSearchTerm.toLowerCase();
        // Weighted search: exact match start > contains > fuzzy
        const matches = iconList.filter(icon => icon.toLowerCase().includes(term))
            .sort((a, b) => {
                const aStarts = a.includes(`devicon-${term}`);
                const bStarts = b.includes(`devicon-${term}`);
                if (aStarts && !bStarts) return -1;
                if (!aStarts && bStarts) return 1;
                return 0;
            })
            .slice(0, 10); // Check top 10
        setSuggestedIcons(matches);
    }, [iconSearchTerm]);

    const handleCustomIconUpload = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setCustomIconFile(e.target.files[0]);
            setSelectedIcon(''); // Clear selection if file is chosen
        }
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        setIsAdmin(false);
        setSession(null);
    };


    return (
        <div className="min-h-screen bg-slate-900 text-white font-sans selection:bg-cyan-500 selection:text-white">
            {/* Scroll Progress Bar */}
            <div className="fixed top-0 left-0 w-full h-1 bg-slate-800 z-50">
                <div className="h-full bg-gradient-to-r from-cyan-400 to-purple-500 transition-all duration-100" style={{ width: `${scrollProgress}%` }}></div>
            </div>

            {/* Admin Toggle (Hidden/Secret) - Can keep somewhere discreet or in Navbar? Original was fixed bottom left */}
            {!isAdmin ? (
                <button
                    onClick={() => setShowLogin(true)}
                    className="fixed bottom-4 left-4 z-50 p-2 bg-slate-800/50 rounded-full text-slate-600 hover:text-cyan-500 transition-colors opacity-0 hover:opacity-100"
                >
                    <LogIn size={16} />
                </button>
            ) : (
                <button
                    onClick={handleLogout}
                    className="fixed bottom-4 left-4 z-50 p-3 bg-red-600/80 rounded-full text-white shadow-lg hover:bg-red-500 transition-all"
                    title="Cerrar sesión"
                >
                    <LogOut size={20} />
                </button>
            )}

            {/* Login Modal */}
            {showLogin && (
                <Login
                    onLogin={(status) => {
                        setIsAdmin(status);
                        setShowLogin(false);
                    }}
                    onClose={() => setShowLogin(false)}
                />
            )}

            {/* Cropper Modal */}
            {showCropper && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <div className="bg-slate-900 rounded-2xl w-full max-w-lg border border-slate-700 overflow-hidden flex flex-col h-[80vh] md:h-auto">
                        <div className="p-4 border-b border-slate-700 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-white">Ajustar Foto</h3>
                            <button onClick={() => setShowCropper(false)} className="text-slate-400 hover:text-white">X</button>
                        </div>
                        <div className="relative w-full h-64 md:h-96 bg-gray-900 overflow-hidden">
                            <Cropper
                                image={photoToCrop}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                                cropShape="round"
                                showGrid={false}
                                restrictPosition={false} // Allow free movement
                            />
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="text-xs text-slate-400 mb-1 block">Zoom</label>
                                <input
                                    type="range"
                                    value={zoom}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    aria-labelledby="Zoom"
                                    onChange={(e) => setZoom(e.target.value)}
                                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                                />
                            </div>
                            <div className="flex gap-4">
                                <button
                                    onClick={processCroppedImage}
                                    className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                                    disabled={uploadingPhoto}
                                >
                                    {uploadingPhoto ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : 'Confirmar & Subir'}
                                </button>
                                <button onClick={() => setShowCropper(false)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 rounded-lg transition-colors">Cancelar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Navbar
                t={t}
                scrollToSection={scrollToSection}
                portfolio={portfolio}
                currentLang={currentLang}
                changeLang={changeLang}
                scrolled={scrolled}
                isMobileMenuOpen={isMobileMenuOpen}
                setIsMobileMenuOpen={setIsMobileMenuOpen}
            />

            <main role="main">
                <HeroSection
                    t={t}
                    scrollToSection={scrollToSection}
                    portfolio={portfolio}
                    currentLang={currentLang}
                    getText={getText}
                    isAdmin={isAdmin}
                    editingPersonal={editingPersonal}
                    setEditingPersonal={setEditingPersonal}
                    tempPersonal={tempPersonal}
                    setTempPersonal={setTempPersonal}
                    savePersonalInfo={savePersonalInfo}
                    handleFileSelect={(e) => handleFileSelect(e, 'personal')}
                    uploadingPhoto={uploadingPhoto}
                    saving={saving}
                />

                <AboutSection
                    t={t}
                    portfolio={portfolio}
                    currentLang={currentLang}
                    getText={getText}
                    isAdmin={isAdmin}
                    newSkill={newSkill}
                    setNewSkill={setNewSkill}
                    iconSearchTerm={iconSearchTerm}
                    setIconSearchTerm={setIconSearchTerm}
                    suggestedIcons={suggestedIcons}
                    selectedIcon={selectedIcon}
                    setSelectedIcon={setSelectedIcon}
                    handleCustomIconUpload={handleCustomIconUpload}
                    addSkill={addSkill}
                    deleteSkill={deleteSkill}
                    setShowAddSkill={setShowAddSkill}
                    showAddSkill={showAddSkill}

                    handleEditSkill={handleEditSkill}
                    editingSkillId={editingSkillId}
                    setEditingSkillId={setEditingSkillId}
                />

                <ExperienceSection
                    t={t}
                    portfolio={portfolio}
                    currentLang={currentLang}
                    getText={getText}
                    isAdmin={isAdmin}
                    showAddExperience={showAddExperience}
                    setShowAddExperience={setShowAddExperience}
                    newExperience={newExperience}
                    setNewExperience={setNewExperience}
                    addExperience={addExperience}
                    handleEditExperience={handleEditExperience}
                    deleteExperience={deleteExperience}
                    editingExperienceId={editingExperienceId}
                    setEditingExperienceId={setEditingExperienceId}
                />

                <ProjectsSection
                    techIcons={techIcons}
                    t={t}
                    portfolio={portfolio}
                    currentLang={currentLang}
                    getText={getText}
                    isAdmin={isAdmin}
                    showAddProject={showAddProject}
                    setShowAddProject={setShowAddProject}
                    newProject={newProject}
                    setNewProject={setNewProject}
                    addProject={addProject}
                    handleEditProject={handleEditProject}
                    deleteProject={deleteProject}
                    editingProjectId={editingProjectId}
                    setEditingProjectId={setEditingProjectId}
                    crop={crop}
                    setCrop={setCrop}
                    zoom={zoom}
                    setZoom={setZoom}
                    onCropComplete={onCropComplete}
                    projectImageFile={projectImageFile}
                    setProjectImageFile={setProjectImageFile}
                    handleFileSelect={(e) => handleFileSelect(e, 'project')}
                    processCroppedImage={processCroppedImage}
                    uploadingPhoto={uploadingPhoto}
                />

                <ContactSection
                    t={t}
                    portfolio={portfolio}
                    currentLang={currentLang}
                    getText={getText}
                />
            </main>

            <Footer name={portfolio.personal.name} t={t} />

        </div>
    );
}

